import config from '@/config'
import { Platform } from '@/helper/const'
import { i18n } from '@/main'
import { ISearchHighLighText } from '@/models/search'
import { Capacitor } from '@capacitor/core'
import { isGokollab } from '@ghl/clientportal-core/init'
import axios from 'axios'
import { convertToRelativeTime } from './dateTimeConversion'

const nonAcceptableFileTypes = new Set([
  'application/x-msdownload',
  // 'application/zip',
  'dmg',
  'exe',
  // 'zip',
  'bat',
  'apk',
  'csh',
  'ipa',
  'msi',
  'reg',
  'jar',
  'cmd',
  'bin',
  'com',
])

export const getFileType = (filename: string): string => {
  const parts = filename.split('.')
  const extension = parts[parts.length - 1]
  return extension
}

export const acceptableFile = (file: File | null): boolean => {
  if (!file) return false

  const fileType = file.type

  const fileName = file.name
  const typeFromName = getFileType(fileName)
  if (
    nonAcceptableFileTypes.has(fileType) ||
    nonAcceptableFileTypes.has(typeFromName)
  ) {
    return false
  }

  return true
}

export const getVideoMeta = async (
  urlString: string
): Promise<{ [key: string]: string } | null> => {
  const url = new URL(urlString)
  const hostname = url.hostname
  let videoMeta = null
  if (hostname.includes('loom.com')) {
    const { data } = await axios.get(
      `https://www.loom.com/v1/oembed?url=${urlString}`
    )
    videoMeta = data
  } else if (hostname.includes('vimeo.com')) {
    const { data } = await axios.get(
      `https://vimeo.com/api/oembed.json?url=${urlString}`
    )
    videoMeta = data
  } else if (['www.youtube.com', 'youtu.be'].includes(hostname)) {
    const { data } = await axios.get(
      `https://www.youtube.com/oembed?format=json&url=${urlString}`
    )
    videoMeta = data
  }
  return videoMeta
}

export const isMobile = (): boolean => {
  const mediaQuery = window.matchMedia('(max-width: 767px)')
  return mediaQuery.matches
}

export const replaceBucketUrlWithCdnUrl = (value: any): any => {
  try {
    if (
      !value ||
      (typeof value === 'string' && value.includes(config.firebase.assetsUrl))
    ) {
      return value
    }

    let assetUrl: string
    if (typeof value === 'object') {
      assetUrl = value.backgroundImage
    } else {
      assetUrl = value
    }
    //check if blob is present in membership bucket
    const membershipBucketIndex = assetUrl.indexOf(
      config.firebase.membershipStorageBucket
    )
    //check if blob is present in central bucket
    const parentBucketIndex = assetUrl.indexOf(config.firebase.storageBucket)

    //check if url is already a central bucket cdn url
    const cdnIndex = Math.max(
      assetUrl.indexOf(config.firebase.cdnUrl),
      assetUrl.indexOf(config.firebase.oldCdnUrl)
    )

    //check if url is already a membership bucket cdn url
    const membershipCdnIndex = assetUrl.indexOf(
      config.firebase.membershipCdnUrl
    )

    let newAssetUrl

    //if its an url the lets append the cdn url
    if (membershipBucketIndex !== -1 || parentBucketIndex !== -1) {
      // identify the bucket name
      const bucket =
        membershipBucketIndex > -1
          ? config.firebase.membershipStorageBucket
          : config.firebase.storageBucket

      // identify the cdn url to append
      const bucketCdnUrl =
        membershipBucketIndex > -1
          ? config.firebase.membershipCdnUrl
          : config.firebase.cdnUrl

      // find out the bucket string position in the url
      const bucketIndex = Math.max(membershipBucketIndex, parentBucketIndex)
      const length = bucket.length
      let startIndex = bucketIndex + length

      // remove the google cloud url and append the cdn url
      if (assetUrl.substring(startIndex, startIndex + 2) === '/o') {
        startIndex += 2
      }
      newAssetUrl = `${bucketCdnUrl}${assetUrl.substring(
        startIndex,
        assetUrl.length
      )}`
    } else if (cdnIndex === -1 && membershipCdnIndex === -1) {
      // if its a path,its from new bucket and we append membership cdn url
      newAssetUrl = config.firebase.membershipCdnUrl + value
    }
    if (typeof value === 'object') {
      return membershipBucketIndex !== -1 || parentBucketIndex !== -1
        ? { backgroundImage: `url(${newAssetUrl}` }
        : value
    }

    return cdnIndex !== -1 || membershipCdnIndex !== -1 ? value : newAssetUrl
  } catch (error) {
    console.error(error)
    return value
  }
}

export const hidePageLoaderAnimation = (): void => {
  const element = document.getElementById('animation') as any
  element?.parentNode?.removeChild(element)
}

export const getHighlightedText = (
  text: ISearchHighLighText[],
  isDarkMode: boolean
) => {
  return text
    .map(text => {
      return text.type === 'hit'
        ? `<span class="bg-[#FEF0C7] ${
            isDarkMode || text.value === 'everyone</span>' ? 'text-black' : ''
          }">${text.value}</span>`
        : text.value
    })
    .join(' ')
}

export const getEncodedUrl = (url: string | null | undefined): string => {
  if (!url) return ''

  try {
    const lastSlashIndex = url.lastIndexOf('/')
    if (lastSlashIndex === -1) return url

    const baseUrl = url.substring(0, lastSlashIndex + 1)
    const filename = url.substring(lastSlashIndex + 1)

    if (!filename) return url

    return baseUrl + encodeURIComponent(filename)
  } catch (error) {
    console.error('Error encoding URL:', error)
    return url || ''
  }
}

export const getGokollabDomain = () => {
  if (import.meta.env.VITE_JS_ENV === 'production') {
    return 'https://www.gokollab.com'
  } else if (import.meta.env.VITE_JS_ENV === 'staging') {
    return 'https://www.staging.gokollab.com'
  } else {
    return 'http://localhost:3031'
  }
}

export const getCanonicalUrl = route => {
  let canonicalUrl = ''
  if (isGokollab()) {
    canonicalUrl = `${getGokollabDomain()}${route.path}`
  } else {
    canonicalUrl = `${window.location.protocol}//${window.location.host}${route.path}`
  }

  canonicalUrl = canonicalUrl
    .replace(/\/$/, '') // Remove trailing slash
    .split('?')[0] // Remove query parameters
    .toLowerCase() // Convert to lowercase

  return canonicalUrl
}

/**
 * Handle the visibility of the editor
 * @param value - The visibility value
 *
 * This is because, during webview resize when keyboard opens in iOS, the background of the app is visible
 * which is not a good experience.
 */
export const handleEditorVisibility = (value: boolean) => {
  if (Capacitor.getPlatform() != Platform.IOS) {
    return
  }
  console.log('handleEditorVisibility :: ', value)
  if (value) {
    console.log('Editor opened')
    const appElement = document.getElementById('app')
    if (appElement) {
      appElement.style.visibility = 'hidden'
    }
  } else {
    console.log('Editor closed')
    const appElement = document.getElementById('app')
    if (appElement) {
      appElement.style.visibility = 'visible'
    }
  }
}

/**
 * Safely check if the device has native keyboard resize capabilities
 * @param keyboardPlugin - The Capacitor KeyboardEvents plugin instance
 * @returns Promise that resolves to a boolean indicating if native keyboard resize is supported
 *
 * This function safely checks if the KeyboardEvents plugin has native keyboard resize
 * capabilities and handles any potential errors. Returns false if the method fails or is not implemented.
 */
export const checkNativeKeyboardResize = async (
  keyboardPlugin: any
): Promise<boolean> => {
  if (Capacitor.getPlatform() !== Platform.IOS) {
    return false
  }
  if (!keyboardPlugin) {
    console.log('KeyboardEvents plugin not available')
    return false
  }

  try {
    const result = await keyboardPlugin.hasNativeKeyboardResize()
    return !!result?.value
  } catch (error) {
    console.log(
      'Error checking hasNativeKeyboardResize, assuming false:',
      error
    )
    return false
  }
}

/**
 * Get the last activity date
 * @param createdAt - The date the user was created
 * @param lastActivityAt - The date the user last interacted
 * @param lastVisitedAt - The date the user last visited
 * @returns The last activity date
 */
export const getLastActivityDate = (allPassedDates: (string | undefined | Date) []) => {
  // Create array of dates, filter out undefined values, and convert to Date objects
  const dates = allPassedDates.filter(Boolean).map(date => new Date(date!)).filter(dateObj => !isNaN(dateObj.getTime()));
  if(dates.length === 0) {
    return i18n.global.t('userProfilePopover.longAgo')
  }
  const latestDate = dates.sort((a, b) => b.getTime() - a.getTime())[0];
  return convertToRelativeTime(latestDate.toISOString());
}